import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Building2, Sparkles, Scale, BarChart3, Leaf } from "lucide-react";
import Step1CompanyDiscovery from "@/components/steps/Step1CompanyDiscovery";
import Step2HookLayer from "@/components/steps/Step2HookLayer";
import Step3ScalePersonalization from "@/components/steps/Step3ScalePersonalization";
import Step4MarketPressure from "@/components/steps/Step4MarketPressure";

const STEPS = [
  { label: "Company", icon: Building2 },
  { label: "Opportunities", icon: Sparkles },
  { label: "Scale", icon: Scale },
  { label: "Market", icon: BarChart3 },
];

export default function OnboardingPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [userId, setUserId] = useState(null);
  const [companyData, setCompanyData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const uid = localStorage.getItem("olana_user_id");
    if (!uid) {
      navigate("/");
      return;
    }
    setUserId(uid);
  }, [navigate]);

  const goNext = () => {
    if (currentStep < 4) {
      setCurrentStep((s) => s + 1);
    } else if (companyData?.id) {
      localStorage.setItem("olana_company_id", companyData.id);
      navigate(`/dashboard/${companyData.id}`);
    }
  };

  const goBack = () => {
    if (currentStep > 1) setCurrentStep((s) => s - 1);
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return <Step1CompanyDiscovery userId={userId} onComplete={(data) => { setCompanyData(data); localStorage.setItem("olana_company_id", data.id); goNext(); }} />;
      case 2:
        return <Step2HookLayer companyId={companyData?.id} onComplete={goNext} onBack={goBack} />;
      case 3:
        return <Step3ScalePersonalization companyId={companyData?.id} onComplete={goNext} onBack={goBack} />;
      case 4:
        return <Step4MarketPressure companyId={companyData?.id} industry={companyData?.industry} onComplete={goNext} onBack={goBack} />;
      default:
        return null;
    }
  };

  const progressWidth = ((currentStep - 1) / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-screen relative">
      {/* Background image with fade */}
      <div
        className="fixed inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url('/bg-nature.jpg')` }}
      />
      <div className="fixed inset-0 bg-black/80" />

      {/* Aurora glow */}
      <div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-primary/3 blur-[120px] pointer-events-none" />

      {/* Header */}
      <div className="sticky top-0 z-20 bg-background/60 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Leaf className="w-5 h-5 text-primary" />
              <span className="text-lg font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Olana</span>
            </div>
            <span className="text-sm text-muted-foreground">Step {currentStep} of {STEPS.length}</span>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2" data-testid="step-indicator">
            {STEPS.map((step, i) => {
              const StepIcon = step.icon;
              const isActive = i + 1 <= currentStep;
              return (
                <div key={`step-${step.label}`} className="flex items-center flex-1">
                  <div className={`flex items-center gap-1.5 ${isActive ? 'text-primary' : 'text-muted-foreground/40'}`}>
                    <StepIcon className="w-4 h-4" />
                    <span className="text-xs hidden md:inline">{step.label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="flex-1 mx-2 h-[2px] rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full bg-primary rounded-full transition-all duration-500"
                        style={{ width: i + 1 < currentStep ? '100%' : '0%' }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-16">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -30 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          >
            {renderStep()}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
